import React, {useMemo, useRef, useState} from "react";
import {Toast} from "primereact/toast";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {useSelector} from "react-redux";
import {useTranslate} from "../../system/useTranslate";
import Icon from "../../system/components/icon";
import BriefStepper from "./components/brief-stepper";
import ProductBriefForm from "./components/product-brief-form";
import BriefPreview from "./components/brief-preview";
import PromptPreview from "./components/prompt-preview";
import StrapiPromptPreview from "./components/strapi-prompt-preview";
import ProductBriefActions from "./components/product-brief-actions";
import ProductBriefAiDialog from "./components/product-brief-ai-dialog";
import useProductBriefDraft from "./hooks/use-product-brief-draft";
import {buildCodexPrompt} from "./utils/build-codex-prompt";
import {buildStrapiBackendPrompt} from "./utils/build-strapi-backend-prompt";
import {exportProductBriefDocx} from "./utils/export-product-brief-docx";
import {isBriefValid, meetingExample, validateBrief} from "./utils/product-brief-schema";
import {downloadProductBriefJson, parseProductBriefJsonFileText} from "./utils/product-brief-json";
import {createProductBriefTranslator} from "./translations";
import "./style.css";

function ProductBriefBuilder() {
    const baseTranslate = useTranslate();
    const translate = useMemo(() => createProductBriefTranslator(baseTranslate), [baseTranslate]);
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const toast = useRef();
    const [activeStep, setActiveStep] = useState(0);
    const [exporting, setExporting] = useState(false);
    const [aiDialogVisible, setAiDialogVisible] = useState(false);
    const [pendingImportBrief, setPendingImportBrief] = useState(null);
    const {brief, setBrief, saveDraft, clearDraft, savedLabel, storageAvailable, restored} = useProductBriefDraft();
    const errors = useMemo(() => validateBrief(brief), [brief]);
    const valid = useMemo(() => isBriefValid(brief), [brief]);
    const activeWorkspaceId = workspaceMembershipData?.workspace?.documentId;

    const showToast = (severity, summary) => {
        toast.current?.show({severity, summary: translate(summary), life: 3000});
    };

    const copyText = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            showToast("success", "pbb-copy-success");
        } catch (error) {
            showToast("error", "pbb-copy-error");
        }
    };

    const copyPrompt = () => copyText(buildCodexPrompt(brief, translate));
    const copyStrapiPrompt = () => copyText(buildStrapiBackendPrompt(brief, translate));

    const downloadDocx = async () => {
        if (!valid) return;
        setExporting(true);
        try {
            await exportProductBriefDocx(brief, translate);
            showToast("success", "pbb-export-success");
        } catch (error) {
            showToast("error", "pbb-export-error");
        } finally {
            setExporting(false);
        }
    };

    const exportJson = () => {
        try {
            downloadProductBriefJson(brief);
            showToast("success", "pbb-json-export-success");
        } catch (error) {
            showToast("error", "pbb-json-export-error");
        }
    };

    const importJson = async (event) => {
        const file = event.files?.[0];
        event.options?.clear?.();

        if (!file || !file.name?.toLowerCase().endsWith(".json")) {
            showToast("error", "pbb-json-import-file-type");
            return;
        }

        try {
            const result = parseProductBriefJsonFileText(await file.text());
            if (!result.success) {
                showToast("error", result.errorKey);
                return;
            }
            setPendingImportBrief(result.brief);
        } catch (error) {
            showToast("error", "pbb-json-import-read-error");
        }
    };

    const applyImportedBrief = () => {
        if (!pendingImportBrief) return;
        setBrief(pendingImportBrief);
        setActiveStep(0);
        saveDraft(pendingImportBrief);
        setPendingImportBrief(null);
        showToast("success", "pbb-json-import-success");
    };

    const applyAiBrief = (generatedBrief) => {
        setBrief(generatedBrief);
        setActiveStep(0);
        saveDraft(generatedBrief);
        setAiDialogVisible(false);
        showToast("success", "pbb-ai-apply-success");
    };

    const handleClear = () => {
        if (!window.confirm(translate("pbb-clear-confirm"))) return;
        clearDraft();
        showToast("success", "pbb-draft-cleared");
    };

    return (
        <div className="pbb-page" dir="rtl">
            <Toast ref={toast} position="bottom-left"/>
            <header className="pbb-header">
                <div className="pbb-title-block">
                    <span><Icon icon="mi mi:assignment" size={22}/>{translate("pbb-internal-tool")}</span>
                    <h1>{translate("pbb-title")}</h1>
                    <p>{translate("pbb-description")}</p>
                </div>
                <div className="pbb-header-actions">
                    {restored && <span className="pbb-restored">{translate("pbb-draft-restored")}</span>}
                    <Button type="button" icon="pi pi-copy" label={translate("pbb-copy-prompt")} disabled={!valid} onClick={copyPrompt}/>
                </div>
            </header>

            <BriefStepper activeStep={activeStep} setActiveStep={setActiveStep} translate={translate}/>

            {!storageAvailable && (
                <div className="pbb-warning">
                    <Icon icon="mi mi:warning" size={20}/>
                    <span>{translate("pbb-storage-unavailable-description")}</span>
                </div>
            )}

            <main className="pbb-layout">
                <div className="pbb-main-column">
                    <ProductBriefForm brief={brief} setBrief={setBrief} activeStep={activeStep} setActiveStep={setActiveStep} errors={errors} translate={translate}/>
                    <ProductBriefActions
                        translate={translate}
                        valid={valid}
                        onSave={() => {
                            const saved = saveDraft();
                            showToast(saved ? "success" : "error", saved ? "pbb-draft-saved" : "pbb-storage-unavailable");
                        }}
                        onClear={handleClear}
                        onLoadExample={() => {
                            setBrief(meetingExample);
                            setActiveStep(5);
                            showToast("success", "pbb-example-loaded");
                        }}
                        onCopyPrompt={copyPrompt}
                        onCopyStrapiPrompt={copyStrapiPrompt}
                        onDownload={downloadDocx}
                        onExportJson={exportJson}
                        onImportJson={importJson}
                        onOpenAi={() => setAiDialogVisible(true)}
                        savedLabel={savedLabel}
                        storageAvailable={storageAvailable}
                        exporting={exporting}
                        aiAvailable={!!activeWorkspaceId}
                    />
                </div>
                <aside className="pbb-preview-column">
                    <BriefPreview brief={brief} translate={translate} onCopy={copyText}/>
                    <PromptPreview brief={brief} translate={translate} onCopy={copyText}/>
                    <StrapiPromptPreview brief={brief} translate={translate} onCopy={copyText}/>
                </aside>
            </main>

            <Dialog
                className="pbb-import-dialog rtl"
                header={translate("pbb-json-import-confirm-title")}
                visible={!!pendingImportBrief}
                onHide={() => setPendingImportBrief(null)}
                footer={(
                    <div className="pbb-dialog-footer">
                        <Button type="button" label={translate("pbb-cancel")} text onClick={() => setPendingImportBrief(null)}/>
                        <Button type="button" icon="pi pi-check" label={translate("pbb-json-import-confirm-accept")} onClick={applyImportedBrief}/>
                    </div>
                )}
                modal
            >
                <p className="pbb-import-warning">{translate("pbb-json-import-confirm-description")}</p>
            </Dialog>

            <ProductBriefAiDialog
                visible={aiDialogVisible}
                onHide={() => setAiDialogVisible(false)}
                translate={translate}
                brief={brief}
                workspaceId={activeWorkspaceId}
                onApply={applyAiBrief}
            />
        </div>
    );
}

export default ProductBriefBuilder;
